import React, { ReactNode } from 'react';
import {
  Autocomplete,
  AutocompleteRenderGetTagProps,
  CircularProgress,
  TextField,
} from '@mui/material';
import { debounce } from 'lodash';

interface IProps<T> {
  key?: string;
  label: string;
  loading: boolean;
  value: T[];
  options: T[];
  helperText?: string | string[] | undefined;
  error?: boolean;
  searchFunction: (e: React.ChangeEvent<HTMLInputElement>) => void;
  fetchFunction: (e: React.UIEvent<HTMLUListElement, UIEvent>) => void;
  getOptionLabel: (option: T) => string;
  renderOption: (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: T
  ) => ReactNode;
  onChange: (
    event: React.SyntheticEvent<Element, Event>,
    newValue: T[],
    reason: 'createOption' | 'selectOption' | 'removeOption' | 'blur' | 'clear'
  ) => void;
  onInputChange: (
    event: React.SyntheticEvent<Element, Event>,
    newInputValue: string
  ) => void;
  disabled?: boolean;
  limitTags: number;
  noOptionsText?: string;
  onFocus?: any;

  sx?: any;
  renderTags?: (
    value: T[],
    getTagProps: AutocompleteRenderGetTagProps
  ) => ReactNode;
}

const MultipleAutoComplete = <T extends object>(
  props: IProps<T> & { children?: ReactNode }
) => {
  const {
    key,
    label,
    loading,
    value,
    error,
    helperText,
    fetchFunction,
    options,
    onChange,
    onInputChange,
    renderOption,
    getOptionLabel,
    disabled,
    limitTags,
    noOptionsText,
    searchFunction,
    onFocus,
    sx,
    renderTags,
  } = props;
  const debounceWithSearch = debounce(searchFunction, 1300);

  return (
    <Autocomplete
      key={key}
      defaultValue={value}
      filterOptions={(options) => options}
      onOpen={onFocus}
      onInputChange={onInputChange}
      onChange={onChange}
      id='mac'
      disableCloseOnSelect={true}
      disableClearable
      options={options}
      getOptionLabel={getOptionLabel}
      renderOption={renderOption}
      multiple
      limitTags={limitTags}
      noOptionsText={noOptionsText ? noOptionsText : 'No Options'}
      renderInput={(params) => (
        <>
          <TextField
            {...params}
            error={error}
            helperText={helperText}
            label={label}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <React.Fragment>
                  {loading ? <CircularProgress size={28} /> : null}
                  {params.InputProps.endAdornment}
                </React.Fragment>
              ),
            }}
            onChange={debounceWithSearch}
          />
        </>
      )}
      renderTags={renderTags}
      ListboxProps={{
        onScroll: fetchFunction,
        role: 'list-box',
        style: {
          maxHeight: 300,
        },
      }}
      disabled={disabled}
      sx={sx}
    />
  );
};

export default MultipleAutoComplete;
